import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

async function syncGuestProposalTargetsAfterLogin() {
  try {
    const Http = (await import('../../core/Http')).default;
    await Http.post('/api/glow/invite-context/sync-guest-proposal-targets');
  } catch {
    /* 토큰 미반영·네트워크 실패 등은 무시 (초대 링크 진입 시 한 번 더 시도 가능) */
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    const fetchMyInfo = async () => {
      try {
        const Http = (await import('../../core/Http')).default;
        // 새로고침 시 무조건 백엔드에 쿠키와 함께 현재 권한을 물어봄
        const response = await Http.get('/api/glow/user/me');

        // Http 인터셉터가 response.data만 반환하도록 설정되어 있으므로 바로 세팅
        setUser(response);
        setIsAuthenticated(true);
        await syncGuestProposalTargetsAfterLogin();
      } catch (error) {
        // 토큰이 없거나 만료되었을 경우 401 에러 떨어지고 알아서 로그인 해제됨
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    fetchMyInfo();
  }, []);

  // 메뉴 관련 캐시 완전 초기화 (sessionStorage 및 window 객체)
  const clearMenuCache = () => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('__MENU_DATA_') || key.startsWith('__MENU_LOADING_')) {
        sessionStorage.removeItem(key);
      }
    });
    Object.keys(window).forEach((key) => {
      if (key.startsWith('__MENU_DATA_') || key.startsWith('__MENU_LOADING_')) {
        delete window[key];
      }
    });
  };

  const login = (userData) => {
    // 혹시라도 로그아웃(세션만료 등)을 정상적으로 거치지 않고 강제 로그인 화면 이동 후 다른 아이디로 로그인 시
    // 이전 유저의 메뉴 캐시가 남아있는 것을 방지하기 위해 로그인 시점에도 한 번 지워줍니다.
    clearMenuCache();

    setUser(userData);
    setIsAuthenticated(true);
    // 로그인 응답에는 companyName·프로필 등 /me 전용 필드가 없을 수 있어, 쿠키 반영 후 한 번 동기화
    (async () => {
      try {
        const Http = (await import('../../core/Http')).default;
        const full = await Http.get('/api/glow/user/me');
        if (full && typeof full === 'object') {
          setUser(full);
        }
        await syncGuestProposalTargetsAfterLogin();
      } catch {
        /* 로그인 직후 일시 실패 시에도 최소 userData 유지 */
      }
    })();
  };

  const logout = async () => {
    try {
      // Http 모듈을 사용하여 백엔드 로그아웃 API 호출 (JWT 쿠키 삭제)
      // Http.js가 상단에 임포트되지 않았으므로 동적 임포트 사용 (순환/의존성 문제 방지)
      const Http = (await import('../../core/Http')).default;
      await Http.post('/api/glow/user/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      clearMenuCache();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, isLoading: loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

